import {
  Conversation,
  InlineDataAction,
} from '@stripe-internal/conversations-client';
import {
  BottomDrawer,
  ChatInput,
  Confirmation,
  Log,
  useWidget,
  useWidgetTitle,
  useWidgetRoutingMode,
  useWidgetConversationId,
} from '@stripe-internal/conversations-ui';
import {
  memo,
  useCallback,
  useEffect,
  useMemo,
  useState,
  useContext,
} from 'react';
import {view} from '@sail/ui/deprecated';
import {defineMessages} from 'react-intl';
import {filter, merge} from 'rxjs';
import {useInlineDataEvents} from '@stripe-internal/inline-data';
import {useConversationNotifications} from './useConversationNotifications';
import {EmailModal, RequestACallModal} from '../../Modals';
import {Title} from '../../components/Title';
import {ScreenName, useScreen} from '../../Providers/ScreenProvider';
import {useConversationLogEvents} from './useConversationLogEvents';
import {
  attachmentsEnabled,
  inputEnabled,
  isConversationEligibleForFeedback,
  isConversationEligibleForDotComCSAT,
  isConversationFromAssistant,
  isMessageInteractivityEnabled,
  isRACInProgress,
  userHasInteractedWithConversation,
} from './conversationUtils';
import {useProfile} from '../../Providers/ProfileProvider';
import {ConfigContext} from '../../Providers/ConfigProvider';
import {
  MessageToParentType,
  ParentMessageBusContext,
} from '../../Providers/ParentMessageBusProvider';
import {Topic} from '../../components/TopicSelector';
import {DidWeSolveForm} from '../../components/Forms/Feedback/DidWeSolveForm';
import {Visibility} from '../../Providers/ParentMessageBusProvider/Visibility';
import {useActiveBottomDrawer} from './useActiveBottomDrawer';
import {
  LetsChangeThat,
  LetsChangeThatOption,
} from '../../components/Forms/Feedback/LetsChangeThat';
import {DidWeResolveOption} from '../../Providers/ApiProvider/api/feedback';
import {FeedbackForm} from '../../components/Forms/Feedback/FeedbackForm';
import {useConversationTriggerParams} from './useConversationTriggerParams';
import {useApi} from '../../Providers/ApiProvider/ApiProvider';
import {ReauthenticationModal} from '../../Modals/ReauthenticationModal';
import {CloseConfirmationModal} from './CloseConfirmationModal';
import {useConversationRequestedActions} from './useConversationRequestedActions';
import {useConversationTopBar} from './useConversationTopBar';
import {useConversationGetSuggestedQueries} from './useConversationGetSuggestedQueries';
import {useConversationContextHandoff} from './useConversationContextHandoff';
import {useNewDesignsEnabled} from './useNewDesignsEnabled';
import {buildUHConversationIssueContext} from '../../helpers/buildUHIssueContext';
import {Disclaimer} from './Disclaimer';
import {useConversationClientPending} from './useConversationClientPending';

export const messages = defineMessages({
  closeChatConfirmation: {
    id: 'support_conversations_client.supportWidget.closeChatConfirmation.closeChatDescription',
    description:
      'Instruction provided when user intends to close the chat window',
    defaultMessage: 'Are you sure you want to end this chat?',
  },
  closeChatButtonText: {
    id: 'support_conversations_client.supportWidget.closeChatConfirmation.closeChatButtonText',
    description:
      'Text shown for the close chat button when closing support chat window',
    defaultMessage: 'End the chat',
  },
});

type Props = {
  conversation: Conversation;
  restartConversation: ({
    initialQuery,
  }: {
    initialQuery?: string;
    shouldClear?: boolean;
  }) => Promise<void>;
};

export const ConversationScreen = memo(function ConversationScreen({
  conversation,
  restartConversation,
}: Props) {
  const widget = useWidget();
  const {setTitle} = useWidgetTitle();
  const {setRoutingMode} = useWidgetRoutingMode();
  const {setConversationId} = useWidgetConversationId();
  const {transitionTo} = useScreen();
  const {profile} = useProfile();
  const {entrypoint} = useContext(ConfigContext);
  const api = useApi();
  const newDesignsEnabled = useNewDesignsEnabled();

  const {activeBottomDrawer, showBottomDrawer, closeBottomDrawer} =
    useActiveBottomDrawer({
      conversation,
    });

  useConversationNotifications(conversation);
  useConversationLogEvents({conversation});
  useConversationTriggerParams(conversation);
  useConversationRequestedActions(conversation);
  useConversationGetSuggestedQueries(conversation);
  useConversationTopBar(conversation);
  useConversationContextHandoff(conversation);

  const [testMode, setTestMode] = useState<'dev' | 'synthetic' | undefined>();
  const [showInput, setShowInput] = useState<boolean>(
    inputEnabled(conversation),
  );
  const [disableAttachments, setDisableAttachments] = useState<boolean>(
    !attachmentsEnabled(conversation),
  );
  const [isRACActive, setIsRACActive] = useState<boolean>(
    isRACInProgress(conversation),
  );

  const [disableMessageInteractivity, setDisableMessageInteractivity] =
    useState<boolean>(!isMessageInteractivityEnabled(conversation));

  const pending = useConversationClientPending(conversation);

  // This persists the widget visibility in the conversation context
  // so it can be used to in the useResumeConversation hook on page load
  // to determine if we should automatically open the widget
  useEffect(() => {
    const sub = widget.visibilityObservable().subscribe(() => {
      conversation.setContextValue('visibility', widget.visibility());
    });
    return () => sub.unsubscribe();
  }, [conversation, widget]);

  useEffect(() => {
    if (conversation?.id) {
      setConversationId(conversation.id);
    }
  }, [conversation?.id, setConversationId]);

  const endConversation = useCallback(async () => {
    const isEligibleForDotComCSAT =
      isConversationEligibleForDotComCSAT(conversation);

    if (
      isEligibleForDotComCSAT &&
      widget.visibility() === Visibility.Minimized
    ) {
      conversation.disconnect();
      conversation.leave();
      widget.close(true);
      return;
    }

    if (isEligibleForDotComCSAT) {
      conversation.setContextValue('disableCleanupOnEnd', true);
    }

    // This prevents the chat ended notice from briefly appearing while the widget is closing
    conversation.setContextValue('supressChatEndedNotice', true);

    conversation.leave();

    if (isConversationFromAssistant(conversation)) {
      // close immediately without waiting for the end request to complete
      conversation.disconnect();
      widget.close(true);
    } else if (isEligibleForDotComCSAT) {
      // 2026-03-30 WPP has decided to disable sales chat CSAT for now
      // at the highest level possible, to keep the underlying functionality
      // intact in case CSAT is desired again in the near future.
      // --------------------
      // If sales chat CSAT is to be turned back on, uncomment this line:
      // conversation.setContextValues({dotComCSAT: true});
      // and then delete this line:
      widget.close(true);
      // --------------------
    } else {
      widget.close(true);
    }
  }, [conversation, widget]);

  const {post} = useContext(ParentMessageBusContext);
  const requestOpenEmailModal = useCallback(
    (topic?: Topic) => {
      post({
        type: MessageToParentType.RequestOpenEmailModal,
        data: {
          topic,
        },
      });
    },
    [post],
  );

  useEffect(() => {
    setTitle(<Title testMode={testMode} />);
    setRoutingMode(testMode || 'normal');
  }, [setTitle, testMode, setRoutingMode]);

  const bindToConversationState = useCallback(() => {
    setShowInput(inputEnabled(conversation));
    setDisableAttachments(!attachmentsEnabled(conversation));
    setIsRACActive(isRACInProgress(conversation));
    setDisableMessageInteractivity(
      !isMessageInteractivityEnabled(conversation),
    );
  }, [conversation]);

  useEffect(() => {
    bindToConversationState();
  }, [bindToConversationState]);

  const handleWidgetCloseRequested = useCallback(async () => {
    if (
      isConversationFromAssistant(conversation) ||
      activeBottomDrawer === 'didWeSolve' ||
      ['dashboard', 'onboarding'].includes(entrypoint)
    ) {
      endConversation();
      return;
    }

    if (
      activeBottomDrawer === 'requestACall' ||
      activeBottomDrawer === 'email' ||
      activeBottomDrawer === 'reAuthentication' ||
      isRACActive // in new UI, rac does not use bottom drawer (but widget is still not closable)
    ) {
      widget.minimize();
      return;
    }

    if (
      conversation.context.channel === 'bot' &&
      entrypoint.endsWith('support_site')
    ) {
      endConversation();
      return;
    }

    if (conversation.userIsActive && entrypoint !== 'dot_com') {
      const eligibleForFeedback = await isConversationEligibleForFeedback({
        api,
        conversation,
      });

      if (conversation.context.channel === 'email' && !eligibleForFeedback) {
        endConversation();
        return;
      }

      // Ensure widget is open so user can see confirmation / survey
      if (widget.visibility() === Visibility.Minimized) {
        widget.open();
      }

      // If the user hasn't interacted with the conversation, we should end it immediately
      if (!userHasInteractedWithConversation(conversation)) {
        endConversation();
        return;
      }

      showBottomDrawer(
        eligibleForFeedback ? 'didWeSolve' : 'closeConfirmation',
      );
      return;
    }

    endConversation();
  }, [
    widget,
    conversation,
    api,
    endConversation,
    activeBottomDrawer,
    isRACActive,
    showBottomDrawer,
    entrypoint,
  ]);

  useEffect(() => {
    widget.setOnClose(() => {
      handleWidgetCloseRequested();
      return true;
    });
  }, [widget, handleWidgetCloseRequested]);

  useEffect(() => {
    widget.setOnRestart(async () => {
      restartConversation({});
    });
  }, [widget, restartConversation, conversation]);

  useEffect(() => {
    const sub = conversation.contextObservable().subscribe(() => {
      bindToConversationState();

      const {context} = conversation;
      if (context.dev) {
        setTestMode('dev');
      } else if (context.synthetic) {
        setTestMode('synthetic');
      }
      if (context.dotComCSAT) {
        transitionTo({
          name: ScreenName.DotComCSAT,
          props: {conversation},
        });
      }

      if (context.intent === 'contact_support') {
        if (context.requestedChannel === 'email') {
          showBottomDrawer('email');
        } else if (context.requestedChannel === 'request_a_call') {
          showBottomDrawer('requestACall');
        }
      } else {
        closeBottomDrawer('email');
        closeBottomDrawer('requestACall');
      }
    });

    return () => sub.unsubscribe();
  }, [
    conversation,
    endConversation,
    entrypoint,
    profile.merchant,
    profile.visitor,
    requestOpenEmailModal,
    widget,
    activeBottomDrawer,
    showBottomDrawer,
    closeBottomDrawer,
    transitionTo,
    bindToConversationState,
  ]);

  useEffect(() => {
    const {client} = conversation;

    const clientExceptions$ = client
      .eventsObservable()
      .pipe(filter((e) => e.name === 'client-exception'));
    const clientConversationExceptions$ = client
      .conversationEventsObservable()
      .pipe(filter((e) => e.name === 'conversation-exception'));
    const conversationExceptions$ = conversation
      .eventsObservable()
      .pipe(filter((e) => e.name === 'conversation-exception'));

    const sub = merge(
      clientExceptions$,
      clientConversationExceptions$,
      conversationExceptions$,
    ).subscribe((e) => {
      const cleanupAndShowError = () => {
        conversation.clearStorage();
        transitionTo({
          name: ScreenName.Error,
          props: {sentryEventId: e.sentryEventId},
        });
        conversation.leave();
      };

      if (conversation.client.channel === 'chat') {
        try {
          // attempt to send the agent a message telling them the user
          // has encountered an error and that their conversation
          // has ended before we clear the conversation and transition
          conversation
            .sendMessage(
              conversation.buildTextMessage(
                'Error: The user encountered a client error and has left the conversation.',
              ),
            )
            .then(cleanupAndShowError);
        } catch {
          cleanupAndShowError();
        }
      } else {
        cleanupAndShowError();
      }
    });

    sub.add(
      client.conversationEventsObservable().subscribe(async (event) => {
        if (
          event.name === 'conversation-set-context-values' &&
          !!event.values.restartConversation
        ) {
          const restartConversationParams = event.values.restartConversation;
          restartConversation({
            initialQuery: restartConversationParams.initialQuery,
          });
        }
      }),
    );

    return () => sub.unsubscribe();
  }, [conversation, transitionTo, restartConversation]);

  const onCancelRAC = useCallback(() => {
    if (conversation.hasEnded) {
      widget.close(true);
    }

    conversation.clearContextValues(['intent', 'requestedChannel']);

    if (conversation.context.letsChangeThatFlow) {
      showBottomDrawer('letsChangeThat');
    }
  }, [widget, conversation, showBottomDrawer]);

  const onCancelEmail = useCallback(() => {
    if (conversation.hasEnded) {
      widget.close(true);
    }

    conversation.clearContextValues(['intent', 'requestedChannel']);

    if (conversation.context.letsChangeThatFlow) {
      showBottomDrawer('letsChangeThat');
    }
  }, [widget, conversation, showBottomDrawer]);

  const onDidWeSolveDone = useCallback(
    (didWeSolve: DidWeResolveOption) => {
      closeBottomDrawer('didWeSolve');

      // Show follow-up contact options for unsatisified users
      if (didWeSolve === 'not_resolved') {
        conversation.setContextValues({letsChangeThatFlow: true});
        showBottomDrawer('letsChangeThat');
      } else {
        endConversation();
      }
    },
    [conversation, closeBottomDrawer, showBottomDrawer, endConversation],
  );

  // Helper to determine if we should prefer fullscreen views
  const preferFullscreen = useMemo(() => {
    return (
      newDesignsEnabled &&
      ['dashboard', 'support_site', 'express_support_site'].includes(entrypoint)
    );
  }, [entrypoint, newDesignsEnabled]);

  // CloseConfirmation is the full drawer screen. It's only to be used if the conversation
  // is within a drawer (such as Assistant on the Dashboard). Since this is a straight UI
  // swap, the "activeBottomDrawer" still determines if the full screen confirmation is shown.
  const isFullDrawerCloseConfirmationOpen = useMemo(() => {
    if (!preferFullscreen) {
      return false;
    }

    if (activeBottomDrawer === 'closeConfirmation') {
      return true;
    }

    return false;
  }, [activeBottomDrawer, preferFullscreen]);

  // BottomDrawer is the bottom drawer for the old UH experience and for places where
  // the conversation is not shown in a drawer (such as Assistant/UH on the Support Site)
  const isBottomDrawerOpen = useMemo(() => {
    // Only the close confirmation has been moved to a different experience
    if (preferFullscreen && activeBottomDrawer === 'closeConfirmation') {
      return false;
    }
    return !!activeBottomDrawer;
  }, [activeBottomDrawer, preferFullscreen]);

  const inlineDataEvents = useInlineDataEvents();
  useEffect(() => {
    const sub = inlineDataEvents.observable.subscribe((event) => {
      if (event.name === 'inline-data-action') {
        conversation.action(event as InlineDataAction).then((resp) => {
          inlineDataEvents.emit({
            name: 'inline-data-action-result',
            data: event.data,
            nonce: event.nonce,
            code: resp.code,
            error: resp.error,
          });
        });
      }

      if (event.name === 'inline-data-channel-form-initial-message') {
        inlineDataEvents.emit({
          name: 'inline-data-channel-form-initial-message-result',
          data: {
            initialMessage: conversation.context.latestQuestion || '',
          },
          nonce: event.nonce,
        });
      }

      if (event.name === 'inline-data-trigger-scroll-to-top') {
        conversation.triggerScrollToTop();
      }
      if (event.name === 'inline-data-channel-form-issue-context') {
        inlineDataEvents.emit({
          name: 'inline-data-channel-form-issue-context-result',
          data: {
            issueContext: buildUHConversationIssueContext(conversation),
          },
          nonce: event.nonce,
        });
      }
    });
    return () => sub.unsubscribe();
  }, [conversation, inlineDataEvents]);

  return (
    <view.div
      data-testid="conversation-screen"
      css={{
        stack: 'y',
        height: 'fill',
      }}
      // Setting the `aria-live` attribute so that screen readers can read new messages coming in.
      // We set it to `polite` so that new message don't interrupt the reading of previous messages.
      aria-live="polite"
    >
      {/* CloseConfirmationModal is for use with the conversation in a drawer */}
      <CloseConfirmationModal
        conversation={conversation}
        confirmationType={activeBottomDrawer}
        isOpen={isFullDrawerCloseConfirmationOpen}
        onEndConversation={endConversation}
      />

      {/* BottomDrawer comes first for better tab ordering */}
      <BottomDrawer open={isBottomDrawerOpen}>
        {activeBottomDrawer === 'email' && (
          <EmailModal conversation={conversation} onCancel={onCancelEmail} />
        )}
        {activeBottomDrawer === 'requestACall' && (
          <RequestACallModal
            conversation={conversation}
            onCancel={onCancelRAC}
          />
        )}
        {activeBottomDrawer === 'closeConfirmation' && (
          <Confirmation
            message={messages.closeChatConfirmation}
            confirmLabel={messages.closeChatButtonText}
            onConfirm={() => {
              closeBottomDrawer('closeConfirmation');
              endConversation();
            }}
            onCancel={() => closeBottomDrawer('closeConfirmation')}
          />
        )}
        {activeBottomDrawer === 'didWeSolve' && (
          <DidWeSolveForm
            conversationId={conversation.id}
            onDone={onDidWeSolveDone}
          />
        )}
        {activeBottomDrawer === 'letsChangeThat' && (
          <LetsChangeThat
            onClose={() => {
              closeBottomDrawer('letsChangeThat');
              endConversation();
            }}
            onOptionSelected={(option: LetsChangeThatOption) => {
              closeBottomDrawer('letsChangeThat');
              if (option === 'email') {
                conversation.setContextValues({
                  requestedChannel: 'email',
                  intent: 'contact_support',
                });
              } else if (option === 'request_a_call') {
                conversation.setContextValues({
                  requestedChannel: 'request_a_call',
                  intent: 'contact_support',
                });
              } else if (option === 'chat') {
                conversation.setContextValues({
                  intent: 'contact_support',
                  channel: 'chat',
                  requestedChannel: 'chat',
                  enabledInputModes: undefined,
                });
              } else if (option === 'feedback') {
                showBottomDrawer('feedback');
              }
            }}
          />
        )}
        {activeBottomDrawer === 'feedback' && (
          <FeedbackForm
            conversationId={conversation.id}
            onCancel={() => {
              closeBottomDrawer('feedback');
              showBottomDrawer('letsChangeThat');
            }}
            onDone={() => {
              closeBottomDrawer('feedback');
              endConversation();
            }}
          />
        )}
        {activeBottomDrawer === 'reAuthentication' && <ReauthenticationModal />}
      </BottomDrawer>
      <Log
        conversation={conversation}
        animated
        disableInteraction={disableMessageInteractivity}
      />
      <ChatInput
        conversation={conversation}
        attachmentsDisabled={disableAttachments}
        hidden={!showInput}
        pending={pending}
      />
      {newDesignsEnabled && <Disclaimer />}
    </view.div>
  );
});
